// SearchWithAutocomplete.jsx
import React, { useEffect, useRef, useState } from "react";

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

async function fetchGuestToken() {
  //const cached = sessionStorage.getItem("guest_token");
  // if (cached) return cached;

  const res = await fetch("https://vikingdev.eu/api/auth/guest", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to get guest token");
  const data = await res.json();

  const token = data.token;
  sessionStorage.setItem("guest_token", token);
  return token;
}

async function fetchSuggestions(query, token, signal) {
  const url = `https://vikingdev.eu/api/Search?search=${encodeURIComponent(query)}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error("Search failed");
  return res.json();
}

export default function SearchWithAutocomplete({ onSelect }) {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 250);

  const [token, setToken] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState("");

  const wrapperRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    fetchGuestToken()
      .then((t) => mounted && setToken(t))
      .catch((e) => mounted && setError(e.message));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!token) return;
    const q = debouncedQuery.trim();

    if (q.length < 2) {
      setItems([]);
      setOpen(false);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError("");

    fetchSuggestions(q, token, controller.signal)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.results ?? [];
        setItems(list);
        setOpen(true);
        setActiveIndex(-1);
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setError(e.message);
        setItems([]);
        setOpen(false);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [debouncedQuery, token]);

  const getLabel = (item) =>
    typeof item === "string"
      ? item
      : item?.name ?? item?.title ?? item?.text ?? "";

  const handleSelect = (item) => {
    const label = getLabel(item);
    if(item.type === "Provider") {
        window.open(item.url, "_blank");
        return;
    }
    setSearchQuery(label);
    setOpen(false);

    // ✅ parent will fetch providers using item.id
    onSelect?.(item);

    
  };

  const handleKeyDown = (e) => {
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && items[activeIndex]) {
        e.preventDefault();
        handleSelect(items[activeIndex]);
      } else {
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const returnItemType = (item) => {
    if(item.type === "Provider") {
      return "Ponudnik";
    } else if (item.type === "Subcategory") {
      return "Podkategorija";
    } else if (item.type === "Category") {
      return "Kategorija";
    }
  }

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="flex bg-white rounded-full overflow-hidden shadow-2xl">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Vpiši izdelek, storitev ali podjetje..."
          className="flex-1 px-8 py-6 text-lg outline-none text-gray-800 placeholder-gray-400"
          autoComplete="off"
        />
        {/*<button
          onClick={(e) => {
            setSearchQuery(e.target.value);
            setOpen(true);
          }}
          className="px-10 py-6 text-lg text-white font-semibold"
          style={{
            background: "linear-gradient(135deg, #115461, #0c97af, #2e6f77)",
          }}
        >
          Išči
        </button>*/}
      </div>

      {open && (loading || items.length > 0 || error) && ( 
        <div className="relative left-0 right-0 mt-3 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-[9999]">
          {loading && (
            <div className="px-6 py-4 text-gray-500 text-sm">Nalagam...</div>
          )}

          {!loading && error && (
            <div className="px-6 py-4 text-red-600 text-sm">{error}</div>
          )}

          {!loading && !error && items.length > 0 && (
            <ul className="max-h-80 overflow-auto">
              {items.map((item, idx) => {
                const label =
                  typeof item === "string"
                    ? item
                    : item?.name ??
                      item?.title ??
                      item?.text ??
                      JSON.stringify(item);

                const active = idx === activeIndex;

                return (
                  <li key={item?.id ?? `${label}-${idx}`}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(item)}
                      className={`w-full text-left px-6 py-4 text-gray-800 hover:bg-indigo-50 ${
                        active ? "bg-indigo-50" : ""
                      }`}
                    >
                      {label} / <b>{returnItemType(item)}</b>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
